<template>
  <div class="app-container tender-page">
    <div class="page-head">
      <h1 class="page-title">招标信息</h1>
      <div class="head-actions">
        <el-input v-model.trim="queryParams.keyword" class="keyword-search" placeholder="搜索项目或联系人" prefix-icon="el-icon-search" clearable @keyup.enter.native="handleQuery" @clear="handleQuery" />
        <el-popover v-model="filterVisible" placement="bottom-end" width="280" trigger="click" @show="prepareFilters">
          <el-form class="compact-filter" label-position="top" :model="filterDraft" @submit.native.prevent>
            <el-form-item label="状态"><el-select v-model="filterDraft.status" clearable placeholder="全部状态"><el-option v-for="item in statusOptions" :key="item.value" :label="item.label" :value="item.value" /></el-select></el-form-item>
            <el-form-item label="关键词等级"><el-select v-model="filterDraft.keywordLevel" clearable placeholder="全部等级"><el-option label="A类" value="A" /><el-option label="B类" value="B" /></el-select></el-form-item>
            <el-form-item v-if="canManageTender"><el-checkbox v-model="filterDraft.mineOnly">只看我的</el-checkbox></el-form-item>
            <div class="filter-actions"><el-button size="small" @click="resetQuery">重置</el-button><el-button type="primary" size="small" @click="applyFilters">确定</el-button></div>
            <el-button v-if="canManageTender" type="text" @click="openSources">数据来源</el-button>
          </el-form>
          <el-button slot="reference" icon="el-icon-s-operation" :type="hasExtraFilter ? 'primary' : 'default'" :plain="hasExtraFilter">筛选</el-button>
        </el-popover>
        <el-button v-if="canProcessTender" type="primary" :loading="claiming || currentTaskLoading" @click="handleClaimNext">{{ hasProcessingTask ? '查看当前任务' : '领取新任务' }}</el-button>
      </div>
    </div>

    <section v-loading="statsLoading" class="statistics-overview" aria-label="招投标工作统计">
      <div class="statistics-heading">
        <div>
          <span class="statistics-title">{{ canManageTender && statsScope === 'ALL' ? '团队工作概览' : '我的工作概览' }}</span>
          <span class="statistics-updated">{{ stats.updatedAt ? `更新于 ${parseTime(stats.updatedAt, '{h}:{i}')}` : '实时统计' }}</span>
        </div>
        <div class="statistics-actions">
          <el-radio-group v-if="canManageTender" v-model="statsScope" size="mini" @change="loadStats">
            <el-radio-button label="ALL">全部</el-radio-button>
            <el-radio-button label="MINE">我的</el-radio-button>
          </el-radio-group>
          <el-button v-if="canManageTender" type="text" icon="el-icon-data-analysis" @click="openAnalysis">详细统计</el-button>
        </div>
      </div>
      <div v-if="statsError" class="statistics-error">
        <span>{{ statsError }}</span><el-button type="text" @click="loadStats">重试</el-button>
      </div>
      <div class="statistics-grid">
        <button
          v-for="item in statsCards"
          :key="item.key"
          type="button"
          class="statistics-card"
          :class="[{ active: queryParams.status === item.status }, `tone-${item.tone || 'normal'}`]"
          @click="quickFilter(item.status)"
        >
          <span class="statistics-label">{{ item.label }}</span>
          <strong>{{ statValue(item.key) }}</strong>
          <small>{{ item.hint }}</small>
        </button>
      </div>
    </section>

    <el-tabs class="work-tabs" :value="activeTab" @tab-click="selectTab">
      <el-tab-pane v-for="tab in workTabs" :key="tab.value" :name="tab.value" :label="tab.label" />
    </el-tabs>

    <div class="tender-workspace" :class="{ 'detail-open': detailVisible }">
      <section class="tender-list" aria-label="招标项目清单">
        <div v-if="listError" class="load-error"><span>{{ listError }}</span><el-button type="text" @click="loadList">重试</el-button></div>
        <el-table v-loading="loading" :data="list" :row-class-name="selectedRowClass" @row-click="openRowDetail" empty-text="暂无项目">
          <el-table-column label="项目" min-width="240">
            <template slot-scope="scope">
              <button type="button" class="project-title" @click.stop="openDetail(scope.row.id)">{{ scope.row.title }}</button>
              <div class="project-meta"><span>{{ scope.row.region || '地区未披露' }}</span><span>{{ formatBudget(scope.row) }}</span></div>
            </template>
          </el-table-column>
          <el-table-column label="联系人" width="152">
            <template slot-scope="scope"><div>{{ scope.row.contactName || '未提供' }}</div><a v-if="scope.row.contactPhone" class="phone-link" :href="`tel:${scope.row.contactPhone}`" @click.stop>{{ scope.row.contactPhone }}</a><span v-else class="phone-link muted">未提供电话</span></template>
          </el-table-column>
          <el-table-column label="状态" width="92"><template slot-scope="scope"><el-tag size="small" :type="statusType(scope.row.status)">{{ statusLabel(scope.row.businessId ? 'BUSINESS_CREATED' : scope.row.status) }}</el-tag></template></el-table-column>
          <el-table-column label="操作" width="100" align="center">
            <template slot-scope="scope"><el-button :type="primaryAction(scope.row) === 'review' ? 'primary' : 'text'" size="small" @click.stop="runPrimaryAction(scope.row)">{{ primaryActionLabel(scope.row) }}</el-button></template>
          </el-table-column>
        </el-table>
        <pagination v-show="total > 0" :total="total" :page.sync="queryParams.pageNum" :limit.sync="queryParams.pageSize" @pagination="loadList" />
      </section>

      <aside v-if="detailVisible" class="project-panel" aria-label="项目详情">
        <div class="panel-heading"><h2>项目详情</h2><div>
          <el-dropdown v-if="detail.id && (canManageTender || canEditTender(detail))" trigger="click" @command="handleDetailCommand">
            <el-button type="text" icon="el-icon-more" aria-label="更多项目操作" />
            <el-dropdown-menu slot="dropdown">
              <el-dropdown-item v-if="canEditTender(detail)" command="record">补充联系记录</el-dropdown-item>
              <el-dropdown-item v-if="canManageTender && ['PENDING', 'PROCESSING', 'FOLLOW_UP'].includes(detail.status)" command="assign">{{ detail.status === 'PENDING' ? '分配任务' : '调整负责人' }}</el-dropdown-item>
              <el-dropdown-item v-if="canManageTender && ['PROCESSING', 'FOLLOW_UP'].includes(detail.status)" command="release">收回任务</el-dropdown-item>
            </el-dropdown-menu>
          </el-dropdown>
          <el-button type="text" icon="el-icon-close" aria-label="关闭项目详情" @click="closeDetail" />
        </div></div>
        <div v-loading="detailLoading" class="panel-content">
          <div v-if="detailError" class="load-error"><span>{{ detailError }}</span><el-button type="text" @click="openDetail(selectedId)">重试</el-button></div>
          <template v-if="detail.id">
            <h3 class="panel-project-title">{{ detail.title }}</h3>
            <div class="project-meta">{{ detail.region || '地区未披露' }} · {{ formatBudget(detail) }}</div>
            <el-link v-if="detail.sourceUrl" class="original-link" type="primary" :href="detail.sourceUrl" target="_blank" rel="noopener noreferrer">查看原公告 <i class="el-icon-top-right" /></el-link>
            <dl class="project-facts">
              <template v-if="detail.externalId"><dt>项目编号</dt><dd>{{ detail.externalId }}</dd></template>
              <dt>采购单位</dt><dd>{{ detail.buyerName || '未披露' }}</dd>
              <dt>联系人</dt><dd>{{ detail.contactName || '未提供' }}<a v-if="detail.contactPhone" class="phone-link" :href="`tel:${detail.contactPhone}`">{{ detail.contactPhone }}</a></dd>
              <dt>发布时间</dt><dd>{{ detail.publishTime || '未披露' }}</dd>
              <dt>投标截止</dt><dd>{{ detail.deadline || '未披露' }}</dd>
              <dt>信息来源</dt><dd>{{ detail.sourceName || detail.sourceCode || '未披露' }}</dd>
<template v-if="detail.nextFollowUpAt"><dt>下次联系</dt><dd>{{ parseTime(detail.nextFollowUpAt, '{y}年{m}月{d}日 {h}:{i}') }}</dd></template>
              <template v-if="detail.salesUserName"><dt>销售负责人</dt><dd>{{ detail.salesUserName }}</dd></template>
            </dl>
            <div class="history-heading"><h3>最近联系</h3><el-button v-if="contactRecords.length > 3" type="text" @click="showAllContacts = !showAllContacts">{{ showAllContacts ? '收起' : '全部' }}</el-button></div>
            <div v-loading="contactRecordsLoading" class="contact-history">
              <div v-if="contactRecordsError" class="load-error"><span>联系记录加载失败</span><el-button type="text" @click="loadContactRecords(detail.id)">重试</el-button></div>
              <p v-else-if="!contactRecordsLoading && !contactRecords.length" class="empty-history">暂无联系记录</p>
              <el-timeline v-else>
                <el-timeline-item v-for="item in visibleContactRecords" :key="item.id" :timestamp="item.contactAt" placement="top">
                  <div class="record-person">{{ item.operatorName || '销售助理' }} · {{ contactMethodLabel(item.contactMethod) }}</div>
                  <div class="record-content">{{ item.content }}</div>
                  <div v-if="item.nextAction" class="record-next">{{ item.nextAction }}</div>
                </el-timeline-item>
              </el-timeline>
            </div>
            <el-collapse class="extra-details"><el-collapse-item title="更多项目资料" name="full">
              <el-descriptions :column="1" border>
            <el-descriptions-item label="采购人">{{ detail.buyerName || '-' }}</el-descriptions-item>
            <el-descriptions-item label="行政区域">{{ detail.region || '-' }}</el-descriptions-item>
            <el-descriptions-item label="来源网站">
              <el-link v-if="detail.sourceUrl" type="primary" :href="detail.sourceUrl" target="_blank">{{ detail.sourceName || sourceHost(detail.sourceUrl) }}</el-link>
              <span v-else>{{ detail.sourceName || '-' }}</span>
            </el-descriptions-item>
            <el-descriptions-item label="公告时间">{{ detail.publishTime || '-' }}</el-descriptions-item>
            <el-descriptions-item label="截止时间">{{ detail.deadline || '-' }}</el-descriptions-item>
            <el-descriptions-item label="预算">{{ formatBudget(detail) }}</el-descriptions-item>
            <el-descriptions-item label="联系人">{{ detail.contactName || '-' }} {{ detail.contactPhone || '' }}</el-descriptions-item>
            <el-descriptions-item label="AI方式">{{ detail.aiMethod === 'QWEN' ? '千问判断' : '规则降级' }}</el-descriptions-item>
            <el-descriptions-item label="关键词等级"><el-tag size="small" :type="detail.keywordLevel === 'A' ? 'danger' : 'primary'">{{ detail.keywordLevel || 'B' }}类</el-tag></el-descriptions-item>
            <el-descriptions-item label="命中词">{{ detail.matchedKeywords || '-' }}</el-descriptions-item>
            <el-descriptions-item label="AI摘要" :span="1">{{ detail.aiSummary || '-' }}</el-descriptions-item>
            <el-descriptions-item label="联系人身份">{{ detail.contactRole || '-' }}</el-descriptions-item>
            <el-descriptions-item label="需求方向">{{ detail.demandCategory || '-' }}</el-descriptions-item>
            <el-descriptions-item label="项目阶段">{{ detail.projectStage || '-' }}</el-descriptions-item>
            <el-descriptions-item label="继续沟通">{{ detail.relationshipConfirmed === 1 ? '已确认' : '未确认' }}</el-descriptions-item>
            <el-descriptions-item label="确认需求" :span="1">{{ detail.demandDescription || '-' }}</el-descriptions-item>
            <el-descriptions-item label="下一步动作" :span="1">{{ detail.nextAction || '-' }}</el-descriptions-item>
            <el-descriptions-item label="下次联系">{{ parseTime(detail.nextFollowUpAt, '{y}年{m}月{d}日 {h}:{i}') || '-' }}</el-descriptions-item>
            <el-descriptions-item v-if="detail.status === 'INVALID'" label="无效类型">{{ invalidTypeLabel(detail.invalidType) }}</el-descriptions-item>
            <el-descriptions-item :label="detail.status === 'INVALID' ? '具体原因' : '联系结果'">{{ detail.resultReason || '-' }}</el-descriptions-item>
            <el-descriptions-item label="联系备注" :span="1">{{ detail.resultRemark || '-' }}</el-descriptions-item>
          </el-descriptions>
              <div class="content-title">公告正文</div><div class="source-content">{{ detail.sourceContent || '暂无正文' }}</div>
            </el-collapse-item></el-collapse>
          </template>
        </div>
        <div v-if="detail.id && primaryAction(detail) !== 'detail'" class="panel-footer"><el-button type="primary" @click="runPrimaryAction(detail)">{{ primaryAction(detail) === 'review' ? '记录联系结果' : primaryActionLabel(detail) }}</el-button></div>
      </aside>
    </div>

    <el-dialog title="招投标数据来源" :visible.sync="sourceVisible" width="900px" top="6vh" append-to-body>
      <div v-loading="sourceLoading" class="source-dialog-body">
        <el-alert
          :title="`当前正在采集 ${sourceSiteCount} 个网站，最近一轮已完整验证 ${sourceVerifiedSiteCount} 个；候选清单共 ${sourceCandidateSiteCount} 个网站、${sources.length} 条通道，另有 ${sourcePausedSiteCount} 个网站因接口故障或访问控制暂停。只有列表/API、详情正文和公开附件都可读取才显示成功，采集不到不会算作零命中。`"
          type="info"
          :closable="false"
          show-icon
          class="source-alert"
        />
        <el-collapse v-if="sourceGroups.length" accordion>
          <el-collapse-item v-for="group in sourceGroups" :key="group.name" :name="group.name">
            <template slot="title">
              <span class="source-group-title">{{ group.name }}</span>
              <el-tag size="mini" type="info">{{ group.items.filter(item => item.enabled === 1).length }}/{{ group.items.length }} 条采集/候选通道</el-tag>
            </template>
            <el-table :data="group.items" size="mini" stripe>
              <el-table-column label="网站" min-width="190">
                <template slot-scope="scope">
                  <div class="source-site-name">{{ scope.row.sourceName }}</div>
                  <el-link :href="scope.row.listUrl" target="_blank" type="primary">{{ sourceHost(scope.row.listUrl) }}</el-link>
                </template>
              </el-table-column>
              <el-table-column label="状态" width="90" align="center">
                <template slot-scope="scope"><el-tag size="mini" :type="scope.row.enabled === 1 ? 'success' : 'info'">{{ scope.row.enabled === 1 ? '已启用' : '已停用' }}</el-tag></template>
              </el-table-column>
              <el-table-column label="最近采集" width="155" align="center">
                <template slot-scope="scope">{{ scope.row.lastSyncTime || '尚未采集' }}</template>
              </el-table-column>
              <el-table-column label="采集结果" min-width="220">
                <template slot-scope="scope">
                  <el-tag v-if="scope.row.lastSyncStatus" size="mini" :type="sourceStatusType(scope.row.lastSyncStatus)">{{ sourceStatusLabel(scope.row.lastSyncStatus) }}</el-tag>
                  <span v-else class="source-empty">待首次采集</span>
                  <div v-if="scope.row.lastSyncMessage" class="source-message">{{ scope.row.lastSyncMessage }}</div>
                </template>
              </el-table-column>
            </el-table>
          </el-collapse-item>
        </el-collapse>
        <el-empty v-else-if="!sourceLoading" description="暂无数据来源" />
      </div>
    </el-dialog>

    <el-drawer
      title="招投标统计"
      :visible.sync="analysisVisible"
      size="720px"
      append-to-body
      custom-class="tender-statistics-drawer"
    >
      <div v-loading="analysisLoading" class="analysis-body">
        <div class="analysis-toolbar">
          <div>
            <strong>转化与分配分析</strong>
            <p>转化按所选时间内新入池的项目统计；公平性按当前仍保留归属的任务统计。</p>
          </div>
          <el-radio-group v-model="analysisRange" size="small" @change="loadAnalysis">
            <el-radio-button label="TODAY">今日</el-radio-button>
            <el-radio-button label="SEVEN_DAYS">近7天</el-radio-button>
            <el-radio-button label="THIRTY_DAYS">近30天</el-radio-button>
          </el-radio-group>
        </div>
        <div v-if="analysisError" class="analysis-error">
          <span>{{ analysisError }}</span><el-button type="text" @click="loadAnalysis">重试</el-button>
        </div>
        <template v-else>
          <section class="analysis-section">
            <div class="analysis-section-title"><h3>转化漏斗</h3><span>{{ analysis.rangeLabel }}</span></div>
            <div class="funnel-grid">
              <div v-for="(item, index) in funnelItems" :key="item.key" class="funnel-item">
                <span>{{ item.label }}</span>
                <strong>{{ item.value }}</strong>
                <small v-if="index > 0">{{ formatRate(item.value, funnelItems[index - 1].value) }}</small>
                <small v-else>基准批次</small>
              </div>
            </div>
            <div class="funnel-note">所选批次中无效 {{ analysis.funnel.invalidCount || 0 }} 条；漏斗按项目去重，后续形成线索或商机后仍归入原入池批次。</div>
          </section>

          <section class="analysis-section">
            <div class="analysis-section-title"><h3>A/B 类关键词质量</h3><span>按入池批次</span></div>
            <el-table :data="analysis.keywordLevelStats" size="small" empty-text="所选时间暂无数据">
              <el-table-column label="等级" width="70"><template slot-scope="scope"><el-tag size="mini" :type="scope.row.keywordLevel === 'A' ? 'danger' : 'primary'">{{ scope.row.keywordLevel }}类</el-tag></template></el-table-column>
              <el-table-column prop="entryCount" label="入池" align="right" />
              <el-table-column prop="validCount" label="有效" align="right" />
              <el-table-column label="有效率" align="right"><template slot-scope="scope">{{ formatRate(scope.row.validCount, scope.row.entryCount) }}</template></el-table-column>
              <el-table-column prop="clueCreatedCount" label="线索" align="right" />
              <el-table-column label="线索率" align="right"><template slot-scope="scope">{{ formatRate(scope.row.clueCreatedCount, scope.row.entryCount) }}</template></el-table-column>
              <el-table-column prop="businessCreatedCount" label="商机" align="right" />
            </el-table>
            <el-collapse class="keyword-details">
              <el-collapse-item title="查看具体关键词" name="keywords">
                <el-table :data="analysis.keywordStats" size="mini" max-height="360" empty-text="所选时间暂无关键词数据">
                  <el-table-column prop="keyword" label="关键词" min-width="150" />
                  <el-table-column label="等级" width="64" align="center"><template slot-scope="scope">{{ scope.row.keywordLevel }}类</template></el-table-column>
                  <el-table-column prop="entryCount" label="入池" width="70" align="right" />
                  <el-table-column label="有效率" width="82" align="right"><template slot-scope="scope">{{ formatRate(scope.row.validCount, scope.row.entryCount) }}</template></el-table-column>
                  <el-table-column label="线索率" width="82" align="right"><template slot-scope="scope">{{ formatRate(scope.row.clueCreatedCount, scope.row.entryCount) }}</template></el-table-column>
                </el-table>
              </el-collapse-item>
            </el-collapse>
          </section>

          <section class="analysis-section">
            <div class="analysis-section-title">
              <h3>销售助理分配公平性</h3>
              <span>人数 {{ analysis.fairnessSummary.assistantCount || 0 }} · 总量差 {{ analysis.fairnessSummary.assignedDifference || 0 }} · 最大A/B差 {{ analysis.fairnessSummary.maximumKeywordLevelDifference || 0 }}</span>
            </div>
            <el-alert title="仅统计当前仍拥有“招投标信息池”菜单权限的销售助理；已收回重派的任务不计入原人员，系统领取优先补齐本人较少的关键词类别。" type="info" :closable="false" show-icon />
            <el-table :data="analysis.assigneeStats" size="small" class="fairness-table" empty-text="暂无销售助理数据">
              <el-table-column prop="userName" label="销售助理" min-width="110" fixed="left" />
              <el-table-column prop="assignedCount" label="归属任务" width="76" align="right" />
              <el-table-column prop="aCount" label="A类" width="58" align="right" />
              <el-table-column prop="bCount" label="B类" width="58" align="right" />
              <el-table-column label="A/B差" width="68" align="right"><template slot-scope="scope">{{ Math.abs((scope.row.aCount || 0) - (scope.row.bCount || 0)) }}</template></el-table-column>
              <el-table-column prop="processingCount" label="处理中" width="72" align="right" />
              <el-table-column prop="followUpCount" label="待跟进" width="72" align="right" />
              <el-table-column prop="completedCount" label="已完成" width="72" align="right" />
              <el-table-column prop="overdueCount" label="逾期" width="58" align="right" />
            </el-table>
          </section>

          <section class="analysis-section analysis-two-columns">
            <div>
              <div class="analysis-section-title"><h3>无效原因</h3><span>所选入池批次</span></div>
              <div v-if="analysis.invalidReasonStats.length" class="invalid-reason-list">
                <div v-for="item in analysis.invalidReasonStats" :key="item.invalidType"><span>{{ invalidTypeLabel(item.invalidType) }}</span><strong>{{ item.invalidCount }}</strong></div>
              </div>
              <el-empty v-else :image-size="54" description="暂无无效数据" />
            </div>
            <div>
              <div class="analysis-section-title"><h3>采集运行情况</h3><span>实时</span></div>
              <div class="source-health-grid">
                <div><span>今日入池</span><strong>{{ analysis.sourceHealth.todayCollectedCount || 0 }}</strong></div>
                <div><span>启用网站</span><strong>{{ analysis.sourceHealth.enabledSiteCount || 0 }}</strong></div>
                <div><span>成功网站</span><strong>{{ analysis.sourceHealth.successSiteCount || 0 }}</strong></div>
                <div :class="{ warning: analysis.sourceHealth.failedSiteCount > 0 }"><span>异常网站</span><strong>{{ analysis.sourceHealth.failedSiteCount || 0 }}</strong></div>
              </div>
              <p class="last-sync-time">最近采集：{{ analysis.sourceHealth.lastSyncTime || '尚未采集' }}<template v-if="analysis.sourceHealth.pausedSiteCount"> · 暂停 {{ analysis.sourceHealth.pausedSiteCount }} 个</template></p>
            </div>
          </section>
        </template>
      </div>
    </el-drawer>

    <el-dialog title="记录联系结果" :visible.sync="reviewVisible" width="720px" top="6vh" custom-class="tender-review-dialog" :close-on-click-modal="false" :close-on-press-escape="!submitting" :show-close="!submitting">
      <div class="review-project">{{ reviewProjectTitle }}</div>
      <el-form ref="reviewForm" :model="reviewForm" :rules="reviewRules" label-position="top" :disabled="submitting">
        <el-radio-group v-model="reviewForm.result" class="review-result-switch" @change="changeReviewResult">
          <el-radio-button label="FOLLOW_UP">还需跟进</el-radio-button>
          <el-radio-button label="VALID">确认有需求</el-radio-button>
          <el-radio-button label="INVALID">不合适</el-radio-button>
        </el-radio-group>
        <el-steps v-if="reviewForm.result === 'VALID'" :active="reviewStep" simple class="review-steps"><el-step title="联系情况" /><el-step title="需求信息" /></el-steps>
        <template v-if="reviewStep === 0">
          <el-row :gutter="18" class="review-contact-row">
            <el-col :span="14"><el-form-item label="实际联系时间" prop="contactAt"><el-date-picker v-model="reviewForm.contactAt" type="datetime" value-format="yyyy-MM-dd HH:mm:ss" :picker-options="contactTimePickerOptions" style="width:100%" /></el-form-item></el-col>
            <el-col :span="10"><el-form-item label="联系方式" prop="contactMethod"><el-select v-model="reviewForm.contactMethod" placeholder="请选择" style="width:100%"><el-option v-for="item in contactMethods" :key="item.value" :label="item.label" :value="item.value" /></el-select></el-form-item></el-col>
          </el-row>
          <el-form-item label="接通情况" prop="contactOutcome"><el-radio-group v-model="reviewForm.contactOutcome" class="contact-outcomes"><el-radio v-for="item in contactOutcomes" :key="item.value" :label="item.value">{{ item.label }}</el-radio></el-radio-group></el-form-item>
          <el-form-item label="本次进展" prop="remark"><el-input v-model.trim="reviewForm.remark" type="textarea" :rows="3" maxlength="1000" show-word-limit placeholder="记录这次联系的具体情况" /></el-form-item>
          <el-form-item v-if="reviewForm.result === 'FOLLOW_UP'" label="下次联系时间" required>
            <div class="next-contact-inputs">
              <el-form-item prop="nextFollowUpDate"><el-date-picker v-model="reviewForm.nextFollowUpDate" type="date" format="yyyy年MM月dd日" value-format="yyyy-MM-dd" placeholder="选择日期" :editable="false" style="width:100%" /></el-form-item>
              <el-form-item prop="nextFollowUpTime"><el-time-picker v-model="reviewForm.nextFollowUpTime" format="HH:mm" value-format="HH:mm" :picker-options="{ format: 'HH:mm' }" placeholder="选择时分" style="width:100%" /></el-form-item>
            </div>
          </el-form-item>
        <template v-if="reviewForm.result === 'INVALID'">
          <el-form-item label="无效类型" prop="invalidType">
            <el-select v-model="reviewForm.invalidType" placeholder="请选择无效类型" style="width:100%">
              <el-option v-for="item in invalidTypes" :key="item.value" :label="item.label" :value="item.value" />
            </el-select>
          </el-form-item>
          <el-form-item label="具体原因" prop="reason">
            <el-input v-model.trim="reviewForm.reason" type="textarea" :rows="2" maxlength="255" show-word-limit placeholder="请说明具体哪里无效，例如号码为空号、公告已完成定标等" />
          </el-form-item>
        </template>

        </template>
                <template v-if="reviewForm.result === 'VALID' && reviewStep === 1">
          <el-row :gutter="14">
            <el-col :span="12"><el-form-item label="联系人" prop="contactName"><el-input v-model.trim="reviewForm.contactName" maxlength="100" /></el-form-item></el-col>
            <el-col :span="12"><el-form-item label="联系电话" prop="contactPhone"><el-input v-model.trim="reviewForm.contactPhone" maxlength="100" /></el-form-item></el-col>
          </el-row>
          <el-form-item label="联系人身份" prop="contactRole"><el-input v-model.trim="reviewForm.contactRole" maxlength="100" placeholder="例如：项目技术负责人、采购负责人" /></el-form-item>
          <el-row :gutter="14">
            <el-col :span="12">
              <el-form-item label="需求方向" prop="demandCategory">
                <el-select v-model="reviewForm.demandCategory" placeholder="请选择" style="width:100%">
                  <el-option v-for="item in demandCategories" :key="item" :label="item" :value="item" />
                </el-select>
              </el-form-item>
            </el-col>
            <el-col :span="12">
              <el-form-item label="项目阶段" prop="projectStage">
                <el-select v-model="reviewForm.projectStage" placeholder="请选择" style="width:100%">
                  <el-option v-for="item in projectStages" :key="item" :label="item" :value="item" />
                </el-select>
              </el-form-item>
            </el-col>
          </el-row>
          <el-form-item label="确认需求" prop="demandDescription"><el-input v-model.trim="reviewForm.demandDescription" type="textarea" :rows="2" maxlength="1000" show-word-limit placeholder="填写已向对方确认的真实项目或产品需求" /></el-form-item>
          <el-form-item label="合作意愿" prop="relationshipConfirmed"><el-checkbox v-model="reviewForm.relationshipConfirmed">对方愿意继续业务沟通</el-checkbox></el-form-item>
          <el-form-item label="下一步动作" prop="nextAction"><el-input v-model.trim="reviewForm.nextAction" type="textarea" :rows="2" maxlength="500" show-word-limit placeholder="例如：发送技术资料，并于周五预约方案沟通" /></el-form-item>
        </template>

      </el-form>
      <div slot="footer" class="review-footer">
        <el-button v-if="reviewStep === 1" :disabled="submitting" @click="reviewStep = 0">上一步</el-button>
        <el-button v-else :disabled="submitting" @click="reviewVisible = false">取消</el-button>
        <el-button v-if="reviewForm.result === 'VALID' && reviewStep === 0" type="primary" @click="nextReviewStep">下一步，填写需求</el-button>
        <el-button v-else type="primary" :loading="submitting" @click="submitReview">保存结果</el-button>
      </div>
    </el-dialog>

    <el-dialog title="记录联系进展" :visible.sync="contactVisible" width="640px" :close-on-click-modal="false">
      <el-form ref="contactForm" :model="contactForm" :rules="contactRules" label-width="110px">
        <el-row :gutter="14">
          <el-col :span="12"><el-form-item label="实际联系时间" prop="contactAt"><el-date-picker v-model="contactForm.contactAt" type="datetime" value-format="yyyy-MM-dd HH:mm:ss" :picker-options="contactTimePickerOptions" style="width:100%" /></el-form-item></el-col>
          <el-col :span="12"><el-form-item label="联系方式" prop="contactMethod"><el-select v-model="contactForm.contactMethod" style="width:100%"><el-option v-for="item in contactMethods" :key="item.value" :label="item.label" :value="item.value" /></el-select></el-form-item></el-col>
        </el-row>
        <el-form-item label="接通情况" prop="contactOutcome"><el-select v-model="contactForm.contactOutcome" style="width:100%"><el-option v-for="item in contactOutcomes" :key="item.value" :label="item.label" :value="item.value" /></el-select></el-form-item>
        <el-row :gutter="14">
          <el-col :span="12"><el-form-item label="联系对象"><el-input v-model.trim="contactForm.contactName" maxlength="100" /></el-form-item></el-col>
          <el-col :span="12"><el-form-item label="联系电话"><el-input v-model.trim="contactForm.contactPhone" maxlength="100" /></el-form-item></el-col>
        </el-row>
        <el-form-item label="本次进展" prop="content"><el-input v-model.trim="contactForm.content" type="textarea" :rows="3" maxlength="1000" show-word-limit placeholder="如实记录已尝试联系、对方反馈或内部推进情况" /></el-form-item>
        <el-form-item label="下一步动作"><el-input v-model.trim="contactForm.nextAction" maxlength="500" /></el-form-item>
        <el-form-item label="下次联系时间">
          <div class="next-contact-inputs">
            <el-date-picker v-model="contactForm.nextFollowUpDate" type="date" format="yyyy年MM月dd日" value-format="yyyy-MM-dd" placeholder="选择日期" :editable="false" style="width:100%" />
            <el-time-picker v-model="contactForm.nextFollowUpTime" format="HH:mm" value-format="HH:mm" :picker-options="{ format: 'HH:mm' }" placeholder="选择时分" style="width:100%" />
          </div>
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="contactVisible = false">取消</el-button>
        <el-button type="primary" :loading="submitting" @click="submitContactRecord">保存记录</el-button>
      </span>
    </el-dialog>

    <el-dialog title="调整招标拓展任务" :visible.sync="assignVisible" width="500px" :close-on-click-modal="false">
      <el-form ref="assignForm" :model="assignForm" :rules="assignRules" label-width="90px">
        <el-form-item label="销售助理" prop="assigneeId">
          <UserSelect v-model="assignForm.assigneeId" placeholder="请选择拥有本菜单权限的销售助理" />
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="assignVisible = false">取消</el-button>
        <el-button type="primary" :loading="submitting" @click="submitAssign">确认派发</el-button>
      </span>
    </el-dialog>

    <el-dialog title="交给销售" :visible.sync="convertVisible" width="560px" :close-on-click-modal="false">
      <el-form ref="convertForm" :model="convertForm" :rules="convertRules" label-width="100px">
        <el-form-item label="客户名称" prop="customerName">
          <el-input v-model.trim="convertForm.customerName" maxlength="255" />
        </el-form-item>
        <el-form-item label="联系人" prop="contactName">
          <el-input v-model.trim="convertForm.contactName" maxlength="100" />
        </el-form-item>
        <el-form-item label="联系电话" prop="contactPhone">
          <el-input v-model.trim="convertForm.contactPhone" maxlength="64" />
        </el-form-item>
        <el-form-item label="销售负责人" prop="salesUserId">
          <UserSelect v-model="convertForm.salesUserId" placeholder="请选择接收线索的销售" />
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="convertVisible = false">取消</el-button>
        <el-button type="primary" :loading="submitting" @click="submitConvert">确认交接</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import UserSelect from '@/views/crm/components/userSelect'
import { splitContactTime, joinContactTime, contactTimePayload } from '@/utils/tenderContactTime'
import {
  queryTenderList,
  queryTenderStats,
  queryTenderAnalysis,
  queryTenderSources,
  queryTenderDetail,
  claimNextTender,
  assignTender,
  releaseTender,
  reviewTender,
  queryTenderContactRecords,
  addTenderContactRecord,
  convertTenderClue
} from '@/api/crm/tender'

const emptyTenderStats = () => ({
  pendingCount: null,
  processingCount: null,
  followUpCount: null,
  overdueFollowUpCount: null,
  handoffCount: null,
  currentMonthContactedCount: null,
  currentMonthClueCreatedCount: null,
  currentMonthBusinessCreatedCount: null,
  updatedAt: null
})

const emptyTenderAnalysis = () => ({
  range: 'THIRTY_DAYS',
  rangeLabel: '近30天入池',
  funnel: { entryCount: 0, claimedCount: 0, contactedCount: 0, validCount: 0, clueCreatedCount: 0, businessCreatedCount: 0, invalidCount: 0 },
  keywordLevelStats: [],
  keywordStats: [],
  assigneeStats: [],
  fairnessSummary: { assistantCount: 0, minimumAssignedCount: 0, maximumAssignedCount: 0, assignedDifference: 0, maximumKeywordLevelDifference: 0 },
  invalidReasonStats: [],
  sourceHealth: { enabledSiteCount: 0, successSiteCount: 0, failedSiteCount: 0, pausedSiteCount: 0, todayCollectedCount: 0, lastSyncTime: null }
})

export default {
  name: 'TenderOpportunityPool',
  components: { UserSelect },
  data() {
    return {
      loading: false,
      statsLoading: false,
      statsError: '',
      statsScope: 'ALL',
      stats: emptyTenderStats(),
      analysisVisible: false,
      analysisLoading: false,
      analysisError: '',
      analysisRange: 'THIRTY_DAYS',
      analysis: emptyTenderAnalysis(),
      filterVisible: false,
      filterDraft: { status: 'OPEN', keywordLevel: null, mineOnly: false },
      listError: '',
      detailError: '',
      contactRecordsError: false,
      selectedId: null,
      showAllContacts: false,
      workTabs: [{ value: 'OPEN', label: '待处理' }, { value: 'FOLLOW_UP', label: '待跟进' }, { value: 'VALID', label: '待交接' }, { value: 'DONE', label: '已完成' }],
      claiming: false,
      currentTaskLoading: false,
      submitting: false,
      detailLoading: false,
      sourceLoading: false,
      detailVisible: false,
      sourceVisible: false,
      reviewVisible: false,
      reviewStep: 0,
      reviewProjectTitle: '',
      contactVisible: false,
      contactRecordsLoading: false,
      assignVisible: false,
      convertVisible: false,
      list: [],
      total: 0,
      sources: [],
      currentTask: null,
      detail: {},
      contactRecords: [],
      contactTimePickerOptions: { disabledDate: time => time.getTime() > Date.now() },
      contactMethods: [
        { value: 'PHONE', label: '电话' }, { value: 'WECHAT', label: '微信' },
        { value: 'EMAIL', label: '邮件' }, { value: 'VISIT', label: '拜访' },
        { value: 'OTHER', label: '其他/内部进展' }
      ],
      contactOutcomes: [
        { value: 'NO_ANSWER', label: '未接通/未回复' },
        { value: 'CONTACTED', label: '已联系到对方' },
        { value: 'WRONG_NUMBER', label: '号码无效' },
        { value: 'PROGRESS', label: '内部进展' },
        { value: 'OTHER', label: '其他' }
      ],
      queryParams: { pageNum: 1, pageSize: 10, status: 'OPEN', keywordLevel: null, keyword: '', mineOnly: false },
      allStatusOptions: [
        { value: 'OPEN', label: '全部待处理' },
        { value: 'DONE', label: '全部已完成' },
        { value: 'PENDING', label: '待派发' },
        { value: 'PROCESSING', label: '待处理' },
        { value: 'FOLLOW_UP', label: '待跟进' },
        { value: 'VALID', label: '待交接' },
        { value: 'INVALID', label: '不合适' },
        { value: 'CLUE_CREATED', label: '已交给销售' },
        { value: 'QUALIFIED_CONTACT', label: '全部有效业务联系' },
        { value: 'BUSINESS_CREATED', label: '后续已转商机' },
        { value: 'OVERDUE_FOLLOW_UP', label: '逾期待跟进' },
        { value: 'MONTH_CONTACTED', label: '本月已联系' },
        { value: 'MONTH_CLUE_CREATED', label: '本月创建线索' },
        { value: 'MONTH_BUSINESS_CREATED', label: '本月转为商机' }
      ],
      demandCategories: ['MBR膜系统', '膜组件/膜元件', '膜更换', '膜清洗', '维保服务', '其他污水处理需求'],
      projectStages: ['信息收集', '立项/可研', '方案设计', '招标中', '评标/定标', '实施/交付', '其他'],
      invalidTypes: [
        { value: 'CONTACT_INVALID', label: '联系方式无效' },
        { value: 'BUSINESS_MISMATCH', label: '非目标业务' },
        { value: 'PROJECT_ENDED', label: '项目已结束/截止' },
        { value: 'DUPLICATE', label: '重复信息' },
        { value: 'NO_OPPORTUNITY', label: '暂无有效合作机会' },
        { value: 'OTHER', label: '其他' }
      ],
      reviewForm: {
        id: null, result: 'VALID', invalidType: '', reason: '', remark: '', contactName: '', contactPhone: '',
        contactRole: '', demandCategory: '', demandDescription: '', projectStage: '',
        relationshipConfirmed: false, nextAction: '', nextFollowUpDate: '', nextFollowUpTime: '',
        contactAt: null, contactMethod: 'PHONE', contactOutcome: 'CONTACTED'
      },
      contactForm: {
        tenderId: null, contactAt: null, contactMethod: 'PHONE', contactOutcome: 'NO_ANSWER',
        contactName: '', contactPhone: '', content: '', nextAction: '', nextFollowUpDate: '', nextFollowUpTime: ''
      },
      assignForm: { id: null, assigneeId: null },
      convertForm: { id: null, salesUserId: null, customerName: '', contactName: '', contactPhone: '' },
      reviewRules: {
        result: [{ required: true, message: '请选择处理结果', trigger: 'change' }],
        invalidType: [{ validator: this.validateInvalidType, trigger: 'change' }],
        reason: [{ validator: this.validateInvalidReason, trigger: 'blur' }],
        contactAt: [{ required: true, message: '请填写实际联系时间', trigger: 'change' }],
        contactMethod: [{ required: true, message: '请选择联系方式', trigger: 'change' }],
        contactOutcome: [{ required: true, message: '请选择接通情况', trigger: 'change' }, { validator: this.validateReviewContactOutcome, trigger: 'change' }],
        remark: [{ required: true, message: '请填写本次进展', trigger: 'blur' }],
        contactName: [{ required: true, message: '请填写联系人', trigger: 'blur' }],
        contactPhone: [{ required: true, message: '请填写联系电话', trigger: 'blur' }],
        contactRole: [{ required: true, message: '请填写联系人身份', trigger: 'blur' }],
        demandCategory: [{ required: true, message: '请选择需求方向', trigger: 'change' }],
        demandDescription: [{ required: true, message: '请填写确认需求', trigger: 'blur' }],
        projectStage: [{ required: true, message: '请选择项目阶段', trigger: 'change' }],
        nextAction: [{ required: true, message: '请填写下一步动作', trigger: 'blur' }],
        nextFollowUpDate: [{ required: true, message: '请选择日期', trigger: 'change' }],
        nextFollowUpTime: [{ required: true, message: '请选择时分', trigger: 'change' }],
        relationshipConfirmed: [{ required: true, validator: (rule, value, callback) => value === true ? callback() : callback(new Error('请确认对方愿意继续业务沟通')), trigger: 'change' }]
      },
      contactRules: {
        contactAt: [{ required: true, message: '请填写实际联系时间', trigger: 'change' }],
        contactMethod: [{ required: true, message: '请选择联系方式', trigger: 'change' }],
        contactOutcome: [{ required: true, message: '请选择本次联系事实', trigger: 'change' }],
        content: [{ required: true, message: '请填写本次进展', trigger: 'blur' }]
      },
      assignRules: {
        assigneeId: [{ required: true, type: 'number', message: '请选择销售助理', trigger: 'change' }]
      },
      convertRules: {
        customerName: [{ required: true, message: '请填写客户名称', trigger: 'blur' }],
        salesUserId: [{ required: true, type: 'number', message: '请选择销售负责人', trigger: 'change' }]
      }
    }
  },
  computed: {
    canManageTender() {
      const permissions = (this.$store && this.$store.getters && this.$store.getters.permissions) || []
      return permissions.includes('*:*:*') || permissions.includes('crm:tender:manage')
    },
    canProcessTender() {
      const permissions = (this.$store && this.$store.getters && this.$store.getters.permissions) || []
      return permissions.includes('*:*:*') || permissions.includes('crm:tender:manage') || permissions.includes('crm:tender:list')
    },
    statsCards() {
      if (this.canManageTender) {
        return [
          { key: 'pendingCount', label: this.statsScope === 'ALL' ? '待派发' : '可领取', hint: this.statsScope === 'ALL' ? '当前未分配' : '由系统公平派发', status: 'PENDING' },
          { key: 'processingCount', label: '处理中', hint: '已领取待联系', status: 'PROCESSING' },
          { key: 'followUpCount', label: '待跟进', hint: '已设置下次联系', status: 'FOLLOW_UP' },
          { key: 'overdueFollowUpCount', label: '逾期跟进', hint: '应优先处理', status: 'OVERDUE_FOLLOW_UP', tone: 'warning' },
          { key: 'handoffCount', label: '待交接', hint: '已确认有效需求', status: 'VALID' },
          { key: 'currentMonthClueCreatedCount', label: '本月创建线索', hint: '已交给销售', status: 'MONTH_CLUE_CREATED' },
          { key: 'currentMonthBusinessCreatedCount', label: '本月转商机', hint: '线索后续转化', status: 'MONTH_BUSINESS_CREATED' }
        ]
      }
      return [
        { key: 'pendingCount', label: '可领取', hint: '由系统公平派发', status: 'PENDING' },
        { key: 'processingCount', label: '当前处理中', hint: '一次仅处理一条', status: 'PROCESSING' },
        { key: 'followUpCount', label: '待跟进', hint: '已设置下次联系', status: 'FOLLOW_UP' },
        { key: 'overdueFollowUpCount', label: '逾期跟进', hint: '请优先处理', status: 'OVERDUE_FOLLOW_UP', tone: 'warning' },
        { key: 'currentMonthContactedCount', label: '本月已联系', hint: '按项目去重', status: 'MONTH_CONTACTED' },
        { key: 'currentMonthClueCreatedCount', label: '本月创建线索', hint: '已完成交接', status: 'MONTH_CLUE_CREATED' }
      ]
    },
    funnelItems() {
      const funnel = this.analysis.funnel || {}
      return [
        { key: 'entryCount', label: '入池', value: funnel.entryCount || 0 },
        { key: 'claimedCount', label: '已领取', value: funnel.claimedCount || 0 },
        { key: 'contactedCount', label: '已联系', value: funnel.contactedCount || 0 },
        { key: 'validCount', label: '有效联系', value: funnel.validCount || 0 },
        { key: 'clueCreatedCount', label: '创建线索', value: funnel.clueCreatedCount || 0 },
        { key: 'businessCreatedCount', label: '转为商机', value: funnel.businessCreatedCount || 0 }
      ]
    },
    hasProcessingTask() {
      return Boolean(this.currentTask && this.currentTask.id)
    },
    statusOptions() {
      return this.canManageTender ? this.allStatusOptions : this.allStatusOptions.filter(item => item.value !== 'PENDING')
    },
    activeTab() { return this.workTabs.some(tab => tab.value === this.queryParams.status) ? this.queryParams.status : '' },
    hasExtraFilter() { return Boolean(this.queryParams.keywordLevel || this.queryParams.mineOnly || !this.activeTab) },
    visibleContactRecords() { return this.showAllContacts ? this.contactRecords : this.contactRecords.slice(0, 3) },
    sourceGroups() {
      const categoryOrder = ['全国官方', '省级公共资源', '省级政府采购', '水务环保', '央国企', '代理/聚合', '其他']
      const grouped = this.sources.reduce((result, item) => {
        const category = item.sourceCategory || '其他'
        if (!result[category]) result[category] = []
        result[category].push(item)
        return result
      }, {})
      return Object.keys(grouped)
        .sort((a, b) => {
          const left = categoryOrder.indexOf(a)
          const right = categoryOrder.indexOf(b)
          return (left < 0 ? categoryOrder.length : left) - (right < 0 ? categoryOrder.length : right)
        })
        .map(name => ({ name, items: grouped[name] }))
    },
    sourceSiteCount() {
      return new Set(this.sources.filter(item => item.enabled === 1).map(item => this.sourceHost(item.listUrl).replace(/^www\./, '')).filter(host => host && host !== '-')).size
    },
    sourceCandidateSiteCount() {
      return new Set(this.sources.map(item => this.sourceHost(item.listUrl).replace(/^www\./, '')).filter(host => host && host !== '-')).size
    },
    sourcePausedSiteCount() {
      const enabledHosts = new Set(this.sources.filter(item => item.enabled === 1).map(item => this.sourceHost(item.listUrl).replace(/^www\./, '')).filter(host => host && host !== '-'))
      const pausedHosts = new Set(this.sources.filter(item => item.enabled !== 1).map(item => this.sourceHost(item.listUrl).replace(/^www\./, '')).filter(host => host && host !== '-' && !enabledHosts.has(host)))
      return pausedHosts.size
    },
    sourceVerifiedSiteCount() {
      const hosts = this.sources.reduce((result, item) => {
        const host = this.sourceHost(item.listUrl).replace(/^www\./, '')
        if (!host || host === '-' || item.enabled !== 1) return result
        if (!result[host]) result[host] = []
        result[host].push(item.lastSyncStatus)
        return result
      }, {})
      return Object.keys(hosts).filter(host => hosts[host].length && hosts[host].every(status => status === 'SUCCESS')).length
    }
  },
  created() {
    this.refresh()
  },
  methods: {
    refresh() {
      this.loadList()
      this.loadCurrentTask()
      this.loadStats()
      if (this.analysisVisible) this.loadAnalysis()
    },
    loadStats() {
      const requestId = this._statsRequestId = (this._statsRequestId || 0) + 1
      this.statsLoading = true
      this.statsError = ''
      return queryTenderStats(this.canManageTender ? this.statsScope : 'MINE').then(response => {
        if (requestId !== this._statsRequestId) return
        this.stats = { ...emptyTenderStats(), ...(response.data || {}) }
      }).catch(() => {
        if (requestId !== this._statsRequestId) return
        this.stats = emptyTenderStats()
        this.statsError = '统计加载失败，请重试'
      }).finally(() => { if (requestId === this._statsRequestId) this.statsLoading = false })
    },
    openAnalysis() {
      this.analysisVisible = true
      this.loadAnalysis()
    },
    loadAnalysis() {
      if (!this.canManageTender) return Promise.resolve()
      const requestId = this._analysisRequestId = (this._analysisRequestId || 0) + 1
      this.analysisLoading = true
      this.analysisError = ''
      return queryTenderAnalysis(this.analysisRange).then(response => {
        if (requestId !== this._analysisRequestId) return
        const data = response.data || {}
        this.analysis = {
          ...emptyTenderAnalysis(),
          ...data,
          funnel: { ...emptyTenderAnalysis().funnel, ...(data.funnel || {}) },
          fairnessSummary: { ...emptyTenderAnalysis().fairnessSummary, ...(data.fairnessSummary || {}) },
          sourceHealth: { ...emptyTenderAnalysis().sourceHealth, ...(data.sourceHealth || {}) }
        }
      }).catch(() => {
        if (requestId !== this._analysisRequestId) return
        this.analysis = emptyTenderAnalysis()
        this.analysisError = '详细统计加载失败，请重试'
      }).finally(() => { if (requestId === this._analysisRequestId) this.analysisLoading = false })
    },
    statValue(key) {
      const value = this.stats[key]
      return value === null || value === undefined ? '—' : value
    },
    formatRate(part, total) {
      const numerator = Number(part) || 0
      const denominator = Number(total) || 0
      if (!denominator) return '0%'
      return `${(numerator * 100 / denominator).toFixed(1)}%`
    },
    loadList() {
      const requestId = this._listRequestId = (this._listRequestId || 0) + 1
      this.loading = true
      this.listError = ''
      return queryTenderList({ ...this.queryParams }).then(response => {
        if (requestId !== this._listRequestId) return
        this.list = response.rows || []
        this.total = response.total || 0
      }).catch(() => {
        if (requestId !== this._listRequestId) return
        this.list = []
        this.total = 0
        this.listError = '项目加载失败，请重试'
      }).finally(() => { if (requestId === this._listRequestId) this.loading = false })
    },
    loadSources() {
      this.sourceLoading = true
      queryTenderSources().then(response => { this.sources = response.data || [] }).finally(() => { this.sourceLoading = false })
    },
    openSources() {
      this.filterVisible = false
      this.sourceVisible = true
      this.loadSources()
    },
    loadCurrentTask() {
      this.currentTaskLoading = true
      queryTenderList({ pageNum: 1, pageSize: 1, status: 'PROCESSING', mineOnly: true }).then(response => {
        this.currentTask = (response.rows || [])[0] || null
      }).finally(() => { this.currentTaskLoading = false })
    },
    handleQuery() {
      this.queryParams.pageNum = 1
      this.loadList()
    },
    resetQuery() {
      this.queryParams = { pageNum: 1, pageSize: 10, status: 'OPEN', keywordLevel: null, keyword: '', mineOnly: false }
      this.filterVisible = false
      this.closeDetail()
      this.loadList()
    },
    selectTab(tab) {
      this.queryParams.status = tab.name
      this.closeDetail()
      this.handleQuery()
    },
    prepareFilters() {
      const { status, keywordLevel, mineOnly } = this.queryParams
      this.filterDraft = { status, keywordLevel, mineOnly }
    },
    applyFilters() {
      Object.assign(this.queryParams, this.filterDraft)
      this.filterVisible = false
      this.closeDetail()
      this.handleQuery()
    },
    quickFilter(status) {
      if (status === 'PENDING' && !this.canManageTender) {
        this.msgInfo('待领取任务不能预览，请点击“领取新任务”由系统公平派发')
        return
      }
      this.queryParams.status = status
      if (this.canManageTender) this.queryParams.mineOnly = this.statsScope === 'MINE'
      this.closeDetail()
      this.handleQuery()
    },
    handleClaimNext() {
      if (this.claiming || this.currentTaskLoading) return
      if (this.hasProcessingTask) { this.openDetail(this.currentTask.id); return }
      this.claiming = true
      claimNextTender().then(response => {
        const task = response.data || {}
        this.currentTask = task.id ? task : this.currentTask
        this.msgSuccess('已领取任务')
        this.queryParams.status = 'OPEN'
        this.queryParams.pageNum = 1
        this.refresh()
        if (task.id) this.openDetail(task.id)
      }).finally(() => { this.claiming = false })
    },
    openReview(row) {
      this.reviewStep = 0
      this.reviewProjectTitle = row.title || '招标项目'
      this.reviewForm = {
        id: row.id,
        result: row.status === 'VALID' ? 'VALID' : 'FOLLOW_UP',
        invalidType: row.invalidType || '',
        reason: row.resultReason || '',
        remark: '',
        contactName: row.contactName || '',
        contactPhone: row.contactPhone || '',
        contactRole: row.contactRole || '',
        demandCategory: row.demandCategory || '',
        demandDescription: row.demandDescription || '',
        projectStage: row.projectStage || '',
        relationshipConfirmed: row.relationshipConfirmed === 1,
        nextAction: row.nextAction || '',
        ...splitContactTime(row.nextFollowUpAt),
        contactAt: this.nowDateTime(),
        contactMethod: '',
        contactOutcome: ''
      }
      this.reviewVisible = true
      this.$nextTick(() => this.$refs.reviewForm && this.$refs.reviewForm.clearValidate())
    },
    openAssign(row) {
      this.assignForm = { id: row.id, assigneeId: row.assigneeId || null }
      this.assignVisible = true
      this.$nextTick(() => this.$refs.assignForm && this.$refs.assignForm.clearValidate())
    },
    submitAssign() {
      this.$refs.assignForm.validate(valid => {
        if (!valid || this.submitting) return
        this.submitting = true
        assignTender(this.assignForm).then(() => {
          this.msgSuccess('招标拓展任务已派发')
          this.assignVisible = false
          this.refresh()
          this.refreshOpenDetail()
        }).finally(() => { this.submitting = false })
      })
    },
    handleRelease(row) {
      this.$confirm(`确认收回“${row.title}”的拓展任务并重新进入待派发池？`, '收回拓展任务').then(() => releaseTender(row.id)).then(() => {
        this.msgSuccess('拓展任务已收回')
        this.refresh()
        this.refreshOpenDetail()
      }).catch(() => {})
    },
    validateInvalidType(rule, value, callback) {
      if (this.reviewForm.result === 'INVALID' && !value) callback(new Error('标记无效时请选择无效类型'))
      else callback()
    },
    validateInvalidReason(rule, value, callback) {
      if (this.reviewForm.result === 'INVALID' && (!value || !value.trim())) callback(new Error('标记无效时请填写具体原因'))
      else callback()
    },
    changeReviewResult() {
      this.reviewStep = 0
      this.$nextTick(() => this.$refs.reviewForm && this.$refs.reviewForm.clearValidate())
    },
    validateReviewContactOutcome(rule, value, callback) {
      if (this.reviewForm.result === 'VALID' && value && value !== 'CONTACTED') callback(new Error('确认需求前，请先联系到对方'))
      else callback()
    },
    nextReviewStep() {
      this.$refs.reviewForm.validate(valid => {
        if (!valid) return
        const contactTime = new Date(String(this.reviewForm.contactAt).replace(/-/g, '/')).getTime()
        if (!Number.isFinite(contactTime) || contactTime > Date.now()) { this.msgError('实际联系时间不能晚于当前时间'); return }
        this.reviewStep = 1
        this.$nextTick(() => this.$refs.reviewForm && this.$refs.reviewForm.clearValidate())
      })
    },
    reviewValidationMessage() {
      const form = this.reviewForm
      if (!form.contactAt) return '请填写实际联系时间'
      const contactTime = new Date(String(form.contactAt).replace(/-/g, '/')).getTime()
      if (!Number.isFinite(contactTime) || contactTime > Date.now()) return '实际联系时间不能晚于当前时间'
      if (!form.contactMethod || !form.contactOutcome) return '请选择联系方式和本次联系事实'
      if (!form.remark || !form.remark.trim()) return '请填写本次进展'
      if (form.result === 'FOLLOW_UP') {
        const nextFollowUpAt = joinContactTime(form)
        if (!nextFollowUpAt) return '请选择下次联系日期和时分'
        const nextTime = new Date(nextFollowUpAt.replace(/-/g, '/')).getTime()
        if (!Number.isFinite(nextTime) || nextTime <= Date.now()) return '下次联系时间必须晚于当前时间'
        return ''
      }
      if (form.result !== 'VALID') return ''
      if (form.contactOutcome !== 'CONTACTED') return '确认有效业务联系时，本次结果必须为已联系到对方'
      const required = [
        ['contactName', '请确认联系人姓名'],
        ['contactPhone', '请确认联系电话'],
        ['contactRole', '请填写联系人身份或与项目的关系'],
        ['demandCategory', '请选择需求方向'],
        ['demandDescription', '请填写已确认的真实需求'],
        ['projectStage', '请选择项目阶段'],
        ['nextAction', '请填写明确的下一步动作']
      ]
      const missing = required.find(([field]) => !form[field] || !String(form[field]).trim())
      if (missing) return missing[1]
      if (!form.relationshipConfirmed) return '请确认对方愿意继续业务沟通'
      return ''
    },
    submitReview() {
      if (this.submitting) return
      this.$refs.reviewForm.validate(valid => {
        if (!valid || this.submitting) return
        const validationMessage = this.reviewValidationMessage()
        if (validationMessage) { this.msgError(validationMessage); return }
        this.submitting = true
        const payload = { ...contactTimePayload(this.reviewForm), relationshipConfirmed: this.reviewForm.relationshipConfirmed ? 1 : 0 }
        reviewTender(payload).then(() => {
          this.msgSuccess('联系结果已保存')
          this.reviewVisible = false
          this.refresh()
          this.refreshOpenDetail()
        }).finally(() => { this.submitting = false })
      })
    },
    openConvert(row) {
      this.convertForm = {
        id: row.id,
        salesUserId: null,
        customerName: row.buyerName || '',
        contactName: row.contactName || '',
        contactPhone: row.contactPhone || ''
      }
      this.convertVisible = true
      this.$nextTick(() => this.$refs.convertForm && this.$refs.convertForm.clearValidate())
    },
    submitConvert() {
      this.$refs.convertForm.validate(valid => {
        if (!valid || this.submitting) return
        this.submitting = true
        convertTenderClue(this.convertForm).then(response => {
          this.msgSuccess('已交给销售')
          this.convertVisible = false
          this.refresh()
          this.refreshOpenDetail()
        }).finally(() => { this.submitting = false })
      })
    },
    selectedRowClass({ row }) { return String(row.id) === String(this.selectedId) ? 'selected-project' : '' },
    openRowDetail(row) { this.openDetail(row.id) },
    closeDetail() { this.detailVisible = false; this.selectedId = null; this._detailRequestId = (this._detailRequestId || 0) + 1; this._contactsRequestId = (this._contactsRequestId || 0) + 1 },
    primaryAction(row) {
      if (row.status === 'VALID' && (this.canEditTender(row) || this.canManageTender)) return 'convert'
      if (this.canEditTender(row)) return 'review'
      if (row.clueId) return 'clue'
      return 'detail'
    },
    primaryActionLabel(row) { return { review: '跟进', convert: '交给销售', clue: '查看线索', detail: '查看' }[this.primaryAction(row)] },
    runPrimaryAction(row) {
      const action = this.primaryAction(row)
      if (action === 'review') this.openReview(row)
      else if (action === 'convert') this.openConvert(row)
      else if (action === 'clue') this.goClue(row.clueId)
      else this.openDetail(row.id)
    },
    handleDetailCommand(command) {
      if (command === 'record') this.openContactRecord(this.detail)
      if (command === 'assign') this.openAssign(this.detail)
      if (command === 'release') this.handleRelease(this.detail)
    },
    openDetail(id) {
      const requestId = this._detailRequestId = (this._detailRequestId || 0) + 1
      this.selectedId = id
      this.detailVisible = true
      this.detailLoading = true
      this.detailError = ''
      this.detail = {}
      this.contactRecords = []
      this.showAllContacts = false
      queryTenderDetail(id).then(response => {
        if (requestId === this._detailRequestId) this.detail = response.data || {}
      }).catch(() => {
        if (requestId === this._detailRequestId) this.detailError = '项目详情加载失败'
      }).finally(() => { if (requestId === this._detailRequestId) this.detailLoading = false })
      this.loadContactRecords(id)
    },
    loadContactRecords(id) {
      const requestId = this._contactsRequestId = (this._contactsRequestId || 0) + 1
      this.contactRecordsLoading = true
      this.contactRecordsError = false
      queryTenderContactRecords(id).then(response => {
        if (requestId === this._contactsRequestId) this.contactRecords = response.data || []
      }).catch(() => {
        if (requestId === this._contactsRequestId) this.contactRecordsError = true
      }).finally(() => { if (requestId === this._contactsRequestId) this.contactRecordsLoading = false })
    },
    refreshOpenDetail() { if (this.detailVisible && this.selectedId) this.openDetail(this.selectedId) },
    canEditTender(row) {
      if (!row || !this.canProcessTender || !['PROCESSING', 'FOLLOW_UP', 'VALID'].includes(row.status)) return false
      const userId = this.$store && this.$store.state && this.$store.state.user && this.$store.state.user.userId
      return Boolean(row.assigneeId && userId && String(row.assigneeId) === String(userId))
    },
    nowDateTime() {
      const date = new Date()
      const pad = value => String(value).padStart(2, '0')
      return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`
    },
    openContactRecord(row) {
      this.contactForm = {
        tenderId: row.id, contactAt: this.nowDateTime(), contactMethod: 'PHONE',
        contactOutcome: 'NO_ANSWER', contactName: row.contactName || '',
        contactPhone: row.contactPhone || '', content: '', nextAction: '', nextFollowUpDate: '', nextFollowUpTime: ''
      }
      this.contactVisible = true
      this.$nextTick(() => this.$refs.contactForm && this.$refs.contactForm.clearValidate())
    },
    submitContactRecord() {
      this.$refs.contactForm.validate(valid => {
        if (!valid || this.submitting) return
        const payload = contactTimePayload(this.contactForm)
        if (this.contactForm.nextFollowUpDate || this.contactForm.nextFollowUpTime) {
          if (!payload.nextFollowUpAt) { this.msgError('请选择下次联系日期和时分'); return }
          const nextTime = new Date(payload.nextFollowUpAt.replace(/-/g, '/')).getTime()
          if (!Number.isFinite(nextTime) || nextTime <= Date.now()) { this.msgError('下次联系时间必须晚于当前时间'); return }
        }
        this.submitting = true
        addTenderContactRecord(payload).then(() => {
          this.msgSuccess('联系进展已保存')
          this.contactVisible = false
          this.refresh()
          this.refreshOpenDetail()
        }).finally(() => { this.submitting = false })
      })
    },
    contactMethodLabel(value) {
      const item = this.contactMethods.find(option => option.value === value)
      return item ? item.label : value
    },
    contactOutcomeLabel(value) {
      const item = this.contactOutcomes.find(option => option.value === value)
      return item ? item.label : value
    },
    invalidTypeLabel(value) {
      const item = this.invalidTypes.find(option => option.value === value)
      return item ? item.label : (value ? value : '其他/历史数据')
    },
    goClue(id) {
      this.$router.push(`/crm/clue/detail/${id}`)
    },
    statusLabel(status) {
      const item = this.statusOptions.find(option => option.value === status)
      return item ? item.label : status
    },
    statusType(status) {
      return { PENDING: 'info', PROCESSING: '', FOLLOW_UP: 'warning', VALID: 'success', INVALID: 'danger', CLUE_CREATED: '', BUSINESS_CREATED: 'success' }[status] || 'info'
    },
    sourceHost(url) {
      if (!url) return '-'
      return String(url).replace(/^https?:\/\//i, '').split('/')[0] || '-'
    },
    sourceStatusLabel(status) {
      return { SUCCESS: '成功', RUNNING: '采集中', FAILED: '失败', ERROR: '失败', PAUSED: '暂缓接入' }[status] || status
    },
    sourceStatusType(status) {
      return { SUCCESS: 'success', RUNNING: 'warning', FAILED: 'danger', ERROR: 'danger', PAUSED: 'info' }[status] || 'info'
    },
    formatBudget(row) {
      if (row && row.budgetAmount !== null && row.budgetAmount !== undefined) {
        const amount = Number(row.budgetAmount)
        return amount >= 10000 ? `${(amount / 10000).toFixed(2)}万元` : `${amount.toFixed(2)}元`
      }
      return (row && row.budgetText) || '-'
    }
  }
}
</script>

<style scoped>
.next-contact-inputs { display: grid; grid-template-columns: minmax(0, 1.25fr) minmax(0, 1fr); gap: 12px; }
.next-contact-inputs > .el-form-item { margin-bottom: 0; }
.tender-page { background: #f5f7fa; min-height: calc(100vh - 84px); padding: 28px 24px; color: #25334b; }
.page-head { display: flex; align-items: center; justify-content: space-between; gap: 24px; margin-bottom: 22px; }
.page-title { margin: 0; color: #17233d; font-size: 26px; line-height: 38px; font-weight: 600; }
.head-actions { display: flex; align-items: center; gap: 12px; }
.head-actions .el-button + .el-button { margin-left: 0; }
.keyword-search { width: 300px; }
.compact-filter .el-select { width: 100%; }
.filter-actions { display: flex; justify-content: flex-end; }
.statistics-overview { margin-bottom: 18px; padding: 16px 18px 18px; background: #fff; border: 1px solid #e9edf3; border-radius: 6px; }
.statistics-heading { display: flex; align-items: center; justify-content: space-between; min-height: 28px; margin-bottom: 13px; }
.statistics-title { color: #27364f; font-size: 15px; font-weight: 600; }
.statistics-updated { margin-left: 10px; color: #9aa4b3; font-size: 12px; }
.statistics-actions { display: flex; align-items: center; gap: 14px; }
.statistics-actions .el-button { margin-left: 0; }
.statistics-error { display: flex; align-items: center; justify-content: center; min-height: 76px; color: #e25c55; font-size: 13px; }
.statistics-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(126px, 1fr)); gap: 10px; }
.statistics-card { min-width: 0; padding: 13px 14px 12px; border: 1px solid #e8edf4; border-radius: 5px; background: #fbfcfe; color: #27364f; font: inherit; text-align: left; cursor: pointer; transition: border-color .18s ease, background .18s ease, box-shadow .18s ease; }
.statistics-card:hover { border-color: #a8cff8; background: #f7fbff; box-shadow: 0 3px 10px rgba(64,158,255,.08); }
.statistics-card:focus-visible { outline: 2px solid #409eff; outline-offset: 2px; }
.statistics-card.active { border-color: #409eff; background: #f0f7ff; }
.statistics-card.tone-warning strong { color: #e56a42; }
.statistics-label { display: block; color: #6e7b8f; font-size: 13px; line-height: 20px; }
.statistics-card strong { display: block; margin-top: 3px; color: #20314c; font-size: 25px; line-height: 31px; font-weight: 600; }
.statistics-card small { display: block; margin-top: 2px; color: #a0a9b7; font-size: 11px; line-height: 17px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.work-tabs ::v-deep .el-tabs__header { margin-bottom: 22px; }
.work-tabs ::v-deep .el-tabs__nav-wrap::after { height: 1px; background: #e5eaf1; }
.work-tabs ::v-deep .el-tabs__item { padding: 0 24px; font-size: 15px; height: 46px; }
.tender-workspace { display: flex; align-items: flex-start; gap: 20px; }
.tender-list { flex: 1; min-width: 0; background: #fff; padding: 14px 16px 20px; }
.tender-list ::v-deep .el-table { color: #25334b; font-size: 14px; }
.tender-list ::v-deep .el-table th { background: #f8fafc; color: #606c80; font-weight: 500; height: 48px; }
.tender-list ::v-deep .el-table td { padding: 21px 0; border-bottom-color: #edf0f5; }
.tender-list ::v-deep .el-table .selected-project td { background: #edf5ff; }
.project-title { display: block; max-width: 100%; padding: 0; border: 0; background: transparent; font: inherit; color: #1c2a42; font-weight: 600; line-height: 24px; text-align: left; cursor: pointer; overflow-wrap: anywhere; }
.project-title:hover { color: #409eff; }
.project-title:focus-visible { outline: 2px solid #409eff; outline-offset: 3px; }
.project-meta { margin-top: 7px; color: #7e8a9e; font-size: 13px; line-height: 22px; }
.project-meta span + span::before { content: '·'; margin: 0 7px; }
.project-panel { flex: 0 0 380px; width: 380px; position: sticky; top: 100px; background: #fff; border-radius: 6px; box-shadow: 0 4px 16px rgba(31,45,61,.04); }
.panel-heading { display: flex; justify-content: space-between; align-items: center; padding: 15px 24px; border-bottom: 1px solid #edf0f5; }
.panel-heading h2 { margin: 0; font-size: 18px; font-weight: 600; }
.panel-heading .el-button { font-size: 18px; color: #6c788d; padding: 8px; }
.panel-content { padding: 22px 24px 8px; min-height: 180px; max-height: calc(100vh - 250px); overflow: auto; }
.panel-project-title { font-size: 19px; line-height: 29px; margin: 0; color: #17233d; overflow-wrap: anywhere; }
.original-link { margin-top: 12px; font-size: 13px; }
.project-facts { display: grid; grid-template-columns: 78px minmax(0,1fr); column-gap: 10px; row-gap: 13px; margin: 24px 0; font-size: 13px; line-height: 21px; }
.project-facts dt { color: #8490a3; }
.project-facts dd { margin: 0; color: #536078; overflow-wrap: anywhere; }
.history-heading { display: flex; justify-content: space-between; align-items: center; padding-top: 21px; border-top: 1px solid #edf0f5; }
.history-heading h3 { font-size: 16px; margin: 0 0 20px; }
.contact-history { min-height: 58px; }
.contact-history ::v-deep .el-timeline { padding-left: 0; }
.contact-history ::v-deep .el-timeline-item__timestamp { color: #46536b; font-size: 13px; }
.record-person { color: #7d8a9e; font-size: 12px; }
.record-content { margin-top: 10px; padding: 12px; line-height: 23px; background: #f7f9fc; border-radius: 4px; color: #59667c; white-space: pre-wrap; overflow-wrap: anywhere; }
.record-next { font-size: 12px; color: #7d8a9e; margin-top: 6px; }
.empty-history { color: #8c97a9; font-size: 13px; }
.extra-details { margin-top: 10px; }
.extra-details ::v-deep .el-descriptions { font-size: 12px; }
.extra-details ::v-deep .el-descriptions-item__content { overflow-wrap: anywhere; }
.panel-footer { padding: 16px 24px 22px; background: #fff; border-top: 1px solid #edf0f5; }
.panel-footer .el-button { width: 100%; height: 44px; }
.phone-link { display: block; color: #7e8a9e; font-size: 13px; margin-top: 5px; overflow-wrap: anywhere; }
a.phone-link:hover { color: #409eff; }
.muted { color: #a4aebd; }
.load-error { color: #e25c55; padding: 16px 8px; font-size: 13px; }
@media (min-width: 1600px) { .project-panel { flex-basis: 420px; width: 420px; } }
@media (max-width: 1200px) { .project-panel { position: fixed; top: 84px; bottom: 0; right: 0; z-index: 100; width: 380px; box-shadow: -8px 0 24px rgba(31,45,61,.12); display: flex; flex-direction: column; } .panel-content { flex: 1; max-height: none; } .keyword-search { width: 230px; } .statistics-grid { grid-template-columns: repeat(4, minmax(126px, 1fr)); } }
@media (max-width: 768px) { .tender-page { padding: 18px 12px; } .page-head { align-items: flex-start; flex-direction: column; gap: 14px; } .head-actions { width: 100%; gap: 8px; flex-wrap: wrap; } .keyword-search { width: 100%; } .project-panel { width: 100%; max-width: 420px; } .statistics-heading { align-items: flex-start; gap: 10px; } .statistics-actions { align-items: flex-end; flex-direction: column; gap: 3px; } .statistics-grid { grid-template-columns: repeat(2, minmax(126px, 1fr)); } .work-tabs ::v-deep .el-tabs__item { padding: 0 16px; } }
.source-dialog-body { min-height: 180px; max-height: 72vh; overflow-y: auto; padding-right: 4px; }
.source-alert { margin-bottom: 16px; }
.source-group-title { margin-right: 10px; color: #303133; font-weight: 600; }
.source-site-name { margin-bottom: 3px; color: #303133; font-weight: 500; }
.source-empty { color: #909399; font-size: 12px; }
.source-message { margin-top: 4px; color: #8492a6; font-size: 12px; line-height: 18px; }
.phone-link { color: #409eff; }
.danger-action { color: #f56c6c; }
.sales-name { margin-top: 4px; color: #409eff; font-size: 12px; }
.detail-body { padding: 0 24px 28px; }
.detail-body h2 { margin: 0 0 18px; font-size: 20px; line-height: 30px; }
.detail-actions { margin: 18px 0; }
.contact-title { display: flex; align-items: center; justify-content: space-between; margin: 22px 0 14px; color: #303133; font-weight: 600; }
.contact-hint { color: #909399; font-size: 12px; font-weight: 400; }
.contact-timeline { min-height: 80px; max-height: 460px; overflow-y: auto; padding: 4px 12px 0 2px; }
.contact-record { padding: 12px 14px; border: 1px solid #ebeef5; border-radius: 6px; background: #fff; }
.contact-record-head { display: flex; align-items: center; flex-wrap: wrap; gap: 7px; }
.contact-record-content { margin-top: 8px; color: #303133; line-height: 1.7; white-space: pre-wrap; }
.contact-record-meta { margin-top: 6px; color: #606266; line-height: 1.5; }
.contact-record-created { margin-top: 8px; color: #909399; font-size: 12px; }
.content-title { margin-bottom: 10px; color: #303133; font-weight: 600; }
.source-content { padding: 16px; color: #606266; line-height: 1.9; white-space: pre-wrap; background: #f7f9fc; border-radius: 6px; }
.convert-alert { margin-bottom: 18px; }
</style>

<style>
.tender-review-dialog { max-width: calc(100vw - 32px); border-radius: 8px; overflow: hidden; }
.tender-review-dialog .el-dialog__header { padding: 22px 28px 12px; }
.tender-review-dialog .el-dialog__body { max-height: calc(88vh - 146px); padding: 0 28px 8px; overflow-y: auto; }
.tender-review-dialog .el-dialog__footer { padding: 16px 28px; border-top: 1px solid #edf0f5; background: #fff; }
.tender-review-dialog .review-project { margin: 0 0 20px; color: #8a95a6; line-height: 22px; overflow-wrap: anywhere; }
.tender-review-dialog .review-result-switch { display: flex; width: 100%; margin-bottom: 24px; }
.tender-review-dialog .review-result-switch .el-radio-button { flex: 1; }
.tender-review-dialog .review-result-switch .el-radio-button__inner { width: 100%; padding: 13px 0; }
.tender-review-dialog .review-steps { margin: -4px 0 22px; background: #f7f9fc; padding: 14px 20px; }
.tender-review-dialog .el-form-item { margin-bottom: 22px; }
.tender-review-dialog .el-form-item__label { padding-bottom: 7px; line-height: 22px; color: #536078; }
.tender-review-dialog .contact-outcomes { display: flex; flex-wrap: wrap; gap: 16px 22px; padding: 5px 0; }
.tender-review-dialog .contact-outcomes .el-radio { margin: 0; }
.tender-review-dialog .review-footer { display: flex; justify-content: space-between; }
.tender-review-dialog .el-textarea__inner { resize: vertical; }
.tender-statistics-drawer { max-width: calc(100vw - 28px); }
.tender-statistics-drawer .el-drawer__header { margin-bottom: 0; padding: 22px 26px 17px; border-bottom: 1px solid #edf0f5; color: #24334c; font-size: 18px; font-weight: 600; }
.tender-statistics-drawer .el-drawer__body { overflow-y: auto; background: #f6f8fb; }
.analysis-body { min-height: 300px; padding: 20px 22px 34px; color: #263650; }
.analysis-toolbar { display: flex; align-items: center; justify-content: space-between; gap: 18px; margin-bottom: 17px; }
.analysis-toolbar strong { font-size: 16px; }
.analysis-toolbar p { margin: 7px 0 0; color: #8a96a8; font-size: 12px; line-height: 18px; }
.analysis-error { display: flex; align-items: center; justify-content: center; min-height: 220px; color: #e25c55; }
.analysis-section { margin-bottom: 16px; padding: 18px; background: #fff; border: 1px solid #e9edf3; border-radius: 6px; }
.analysis-section-title { display: flex; align-items: center; justify-content: space-between; gap: 12px; margin-bottom: 14px; }
.analysis-section-title h3 { margin: 0; color: #27364f; font-size: 15px; font-weight: 600; }
.analysis-section-title > span { color: #919cad; font-size: 12px; text-align: right; }
.funnel-grid { display: grid; grid-template-columns: repeat(6, 1fr); gap: 7px; }
.funnel-item { position: relative; min-width: 0; padding: 11px 8px; border-radius: 4px; background: #f5f8fc; text-align: center; }
.funnel-item:not(:last-child)::after { content: ''; position: absolute; top: 25px; right: -6px; z-index: 1; width: 0; height: 0; border-top: 5px solid transparent; border-bottom: 5px solid transparent; border-left: 6px solid #b8c7d9; }
.funnel-item span { display: block; color: #738096; font-size: 12px; white-space: nowrap; }
.funnel-item strong { display: block; margin-top: 5px; color: #263750; font-size: 21px; line-height: 26px; }
.funnel-item small { color: #9ba5b3; font-size: 11px; }
.funnel-note { margin-top: 11px; color: #909bad; font-size: 12px; line-height: 19px; }
.keyword-details { margin-top: 10px; }
.keyword-details .el-collapse-item__header { color: #67758a; font-size: 13px; }
.fairness-table { margin-top: 13px; }
.analysis-two-columns { display: grid; grid-template-columns: 1fr 1fr; gap: 22px; }
.invalid-reason-list > div { display: flex; align-items: center; justify-content: space-between; padding: 9px 2px; border-bottom: 1px solid #eff2f6; color: #647188; font-size: 13px; }
.invalid-reason-list strong { color: #293a54; font-size: 15px; }
.source-health-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; }
.source-health-grid > div { padding: 10px 12px; border-radius: 4px; background: #f5f8fc; }
.source-health-grid span { display: block; color: #7f8a9c; font-size: 12px; }
.source-health-grid strong { display: block; margin-top: 3px; color: #2c3c55; font-size: 20px; }
.source-health-grid .warning strong { color: #e56a42; }
.last-sync-time { margin: 11px 0 0; color: #929dac; font-size: 12px; line-height: 18px; }
@media (max-width: 600px) { .tender-review-dialog .el-col { width: 100%; } .tender-review-dialog .el-dialog__body { padding-left: 18px; padding-right: 18px; } }
@media (max-width: 760px) { .analysis-toolbar { align-items: flex-start; flex-direction: column; } .funnel-grid { grid-template-columns: repeat(3, 1fr); } .funnel-item:nth-child(3)::after { display: none; } .analysis-two-columns { grid-template-columns: 1fr; } }
</style>
